// Global Instructions Rule Applied!

/**
 * InputManager Formatting Utilities
 *
 * This file contains utility functions for formatting and parsing values
 * in the InputManager component system.
 */

import dayjs from 'dayjs'
import { DisplayFormatter, FieldTypeEnum, FieldValue, InputParser } from '../types'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

// ============================================================================
// Number and Currency Formatting
// ============================================================================

/**
 * Format only the integer part; preserve decimals exactly as typed.
 * Also strip any existing commas before re-formatting to avoid "3,0,0,000".
 * @param s - The string value to format
 * @param userTyping - Whether the user is currently typing (affects trailing dot preservation)
 * @returns Formatted string with commas in integer part and preserved decimals
 */
const formatDisplay = (s: string, userTyping: boolean): string => {
  if (!s) return ''

  const sign = s.startsWith('-') ? '-' : ''
  let raw = s.replace(/^-/, '')

  // Remove commas that may be present from previous formatting cycles
  raw = raw.replace(/,/g, '')

  const hasTrailingDot = userTyping && raw.endsWith('.')

  const [intPartRaw, ...rest] = raw.split('.')
  const intDigits = intPartRaw.replace(/\D/g, '')
  const intWithCommas = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  let out = sign + intWithCommas

  if (rest.length > 0) {
    // Keep fractional text as-typed (after removing commas)
    const decPartRaw = rest.join('.')
    out += '.' + decPartRaw
  } else if (hasTrailingDot) {
    // Preserve a trailing "." while typing
    out += '.'
  }

  return out
}

/**
 * Ant Design v5 formatter for InputNumber with stringMode.
 * Preserves partial decimals during typing to allow character-by-character editing.
 * @param value - The current value (number or string)
 * @param info - Ant Design formatter info object with userTyping and input properties
 * @returns Formatted string with commas in integer part and preserved decimals
 */
export const createNumberInputFormatter = () => {
  return (value?: number | string, info?: { userTyping: boolean; input: string }): string => {
    if (info?.userTyping) {
      return formatDisplay(info.input ?? '', true)
    }
    const s = value == null ? '' : String(value)
    return formatDisplay(s, false)
  }
}

/**
 * Parser for InputNumber with stringMode.
 * Strips only commas; leaves the rest so intermediate states survive.
 * @param value - The string value to parse
 * @returns String with commas removed
 */
// antd's InputNumber parser is handed `string | undefined`; the nullish fallback was already here.
export const parseNumberInput = (value: string | undefined): string => (value ?? '').replace(/,/g, '')

/**
 * Default number formatter that adds comma separators for thousands
 * @param value - The number to format
 * @returns Formatted number string with commas
 */
const formatNumber: DisplayFormatter = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) {
    return String(value)
  }

  return numValue.toLocaleString()
}

/**
 * Number formatter that removes decimal places for display
 * @param value - The number to format
 * @returns Formatted number string without decimals
 */
export const formatNumberNoDecimal: DisplayFormatter = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) {
    return String(value)
  }

  return Math.round(numValue).toLocaleString()
}

/**
 * Currency formatter with symbol prefix
 * @param value - The number to format
 * @param symbol - Currency symbol (default: '$')
 * @param showDecimal - Whether to show decimal places (default: true)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: any, symbol: string = '$', showDecimal: boolean = true): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) {
    return String(value)
  }

  const formattedNumber = showDecimal ? numValue.toLocaleString() : Math.round(numValue).toLocaleString()

  return `${symbol}${formattedNumber}`
}

/**
 * Enhanced number formatter with custom formatting options
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string
 */
const formatNumberAdvanced = (
  value: any,
  options: {
    symbol?: string
    showDecimal?: boolean
    decimalPlaces?: number
    thousandSeparator?: string
    decimalSeparator?: string
  } = {}
): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const {
    symbol = '',
    showDecimal = true,
    decimalPlaces = 2,
    thousandSeparator = ',',
    decimalSeparator = '.'
  } = options

  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) {
    return String(value)
  }

  let formattedNumber: string

  if (showDecimal) {
    formattedNumber = numValue.toFixed(decimalPlaces)
  } else {
    formattedNumber = Math.round(numValue).toString()
  }

  // Add thousand separators
  const parts = formattedNumber.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)

  const result = parts.join(decimalSeparator)
  return symbol ? `${symbol}${result}` : result
}

// ============================================================================
// Parsing Functions
// ============================================================================

/**
 * Default number parser that removes commas and converts to number
 * @param value - The string to parse
 * @returns Parsed number or original value if parsing fails
 */
const parseNumber: InputParser = (value: string): number | string => {
  if (!value || typeof value !== 'string') {
    return value
  }

  // Remove commas and other non-numeric characters except decimal point
  const cleanValue = value.replace(/[^0-9.-]/g, '')
  const parsed = parseFloat(cleanValue)

  return isNaN(parsed) ? value : parsed
}

/**
 * Integer parser that removes commas and converts to integer
 * @param value - The string to parse
 * @returns Parsed integer or original value if parsing fails
 */
const parseInteger: InputParser = (value: string): number | string => {
  if (!value || typeof value !== 'string') {
    return value
  }

  // Remove commas and other non-numeric characters
  const cleanValue = value.replace(/[^0-9-]/g, '')
  const parsed = parseInt(cleanValue, 10)

  return isNaN(parsed) ? value : parsed
}

/**
 * Currency parser that removes currency symbols and commas
 * @param value - The string to parse
 * @param symbol - Currency symbol to remove (default: '$')
 * @returns Parsed number or original value if parsing fails
 */
const parseCurrency = (value: string, symbol: string = '$'): number | string => {
  if (!value || typeof value !== 'string') {
    return value
  }

  // Remove currency symbol and commas
  let cleanValue = value.replace(new RegExp(`\\${symbol}`, 'g'), '')
  cleanValue = cleanValue.replace(/,/g, '')

  const parsed = parseFloat(cleanValue)
  return isNaN(parsed) ? value : parsed
}

// ============================================================================
// Display Value Extraction
// ============================================================================

/**
 * Extract display value from select options
 * @param value - The selected value
 * @param options - Array of SelectListEntry objects with label, value, and name
 * @returns Display label or original value
 */
export const getSelectDisplayValue = (
  value: any,
  options: SelectListEntry[],
  mode: 'single' | 'multiple' = 'single'
): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  // Handle array values for multi-select
  if (mode === 'multiple' && Array.isArray(value)) {
    if (value.length === 0) {
      return ''
    }
    const labels = value
      .map((val) => {
        const option = options.find((opt) => opt.value === val)
        return option ? option.label : null
      })
      .filter((label): label is string => label !== null)
    return labels.join(', ')
  }

  // Handle single value
  const option = options.find((opt) => opt.value === value)
  return option ? option.label : String(value)
}

/**
 * Extract display value from company data
 * @param value - Company ID, name, object, or array of company objects
 * @param getCompanyDetailsById - Function to get company details
 * @param mode - Selection mode ('single' or 'multiple')
 * @returns Company name(s) or original value
 */
export const getCompanyDisplayValue = (
  value: any,
  getCompanyDetailsById?: (id: string) => any,
  mode: 'single' | 'multiple' = 'single'
): string => {
  if (!value) {
    return ''
  }

  if (mode === 'multiple') {
    if (!Array.isArray(value) || value.length === 0) {
      return ''
    }

    const names = value
      .map((company) => {
        if (typeof company === 'object' && company !== null) {
          return company.label || company.name || company.value || 'Unknown'
        }
        if (getCompanyDetailsById && typeof company === 'string') {
          const companyDetails = getCompanyDetailsById(company)
          if (companyDetails) {
            return companyDetails.name || companyDetails.legal_name || company
          }
        }
        return String(company)
      })
      .slice(0, 3)

    if (value.length <= 3) {
      return names.join(', ')
    }
    return `${names.join(', ')} +${value.length - 3} more`
  } else {
    // Single mode
    if (Array.isArray(value) && value.length > 0) {
      const company = value[0]
      if (typeof company === 'object' && company !== null) {
        return company.label || company.name || company.value || 'Selected Company'
      }
      if (getCompanyDetailsById && typeof company === 'string') {
        const companyDetails = getCompanyDetailsById(company)
        if (companyDetails) {
          return companyDetails.name || companyDetails.legal_name || company
        }
      }
      return String(company)
    }

    if (typeof value === 'object' && value !== null) {
      return value.label || value.name || value.value || String(value)
    }

    if (getCompanyDetailsById && typeof value === 'string') {
      const companyDetails = getCompanyDetailsById(value)
      if (companyDetails) {
        return companyDetails.name || companyDetails.legal_name || value
      }
    }

    return String(value)
  }
}

/**
 * Extract display value from country data
 * @param value - Country code or name
 * @param countriesData - Optional array of countries to look up names from codes
 * @returns Country name or original value
 */
export const getCountryDisplayValue = (value: any, countriesData?: Array<{ code: string; name: string }>): string => {
  if (!value) {
    return ''
  }

  // If value is an object with label property
  if (typeof value === 'object' && value.label) {
    return value.label
  }

  // If value is a string, check if it's a country code (2 uppercase letters)
  const stringValue = String(value)
  const isCountryCode = /^[A-Z]{2}$/.test(stringValue)

  // If it's a country code and we have countriesData, look up the name
  if (isCountryCode && countriesData && Array.isArray(countriesData)) {
    const country = countriesData.find((c: any) => c.code === stringValue)
    if (country && country.name) {
      return country.name
    }
  }

  return stringValue
}

/**
 * Extract display value from city data
 * @param value - City name, encoded string "CityName|GeoNamesID", or city object {name, label, value, data}
 * @returns City name or original value
 */
export const getCityDisplayValue = (value: any): string => {
  if (!value) {
    return ''
  }

  // Handle city object with name/label properties
  if (typeof value === 'object' && value !== null) {
    return value.name || value.label || ''
  }

  // If value contains encoded format "CityName|GeoNamesID", extract just the city name
  if (typeof value === 'string' && value.includes('|')) {
    return value.split('|')[0]
  }

  return String(value)
}

/**
 * Extract display value from contact data
 * @param value - Contact object or array of contacts
 * @param mode - Selection mode ('single' or 'multiple')
 * @returns Contact name(s) or original value
 */
const getContactDisplayValue = (value: any, mode: 'single' | 'multiple' = 'single'): string => {
  if (!value) {
    return ''
  }

  if (mode === 'single') {
    if (Array.isArray(value) && value.length > 0) {
      return value[0]?.label || value[0]?.name || 'Selected Contact'
    }
    return value?.label || value?.name || String(value)
  } else {
    if (!Array.isArray(value) || value.length === 0) {
      return ''
    }

    const names = value.map((contact) => contact?.label || contact?.name || 'Unknown').slice(0, 3)

    if (value.length <= 3) {
      return names.join(', ')
    }
    return `${names.join(', ')} +${value.length - 3} more`
  }
}

/**
 * Get display value for valuation fields
 * Prefers post-money valuation if available, otherwise shows pre-money valuation
 * @param value - The valuation value (object with preMoneyValue and postMoneyValue)
 * @param config - The field configuration
 * @returns Formatted valuation display value
 */
const getValuationDisplayValue = (value: any, config: any): string => {
  if (!value) {
    return ''
  }

  // Handle object format with preMoneyValue and postMoneyValue
  if (typeof value === 'object' && (value.preMoneyValue !== undefined || value.postMoneyValue !== undefined)) {
    const postMoney = value.postMoneyValue
    const preMoney = value.preMoneyValue

    // Prefer post-money if it has a value, otherwise use pre-money
    const displayValue = postMoney && postMoney !== 0 ? postMoney : preMoney
    const displayType = postMoney && postMoney !== 0 ? 'Post' : 'Pre'

    if (displayValue && displayValue !== 0) {
      const currencySymbol = config.currencySymbol || '$'
      const formattedValue = formatNumber(displayValue)
      return `${currencySymbol}${formattedValue} (${displayType})`
    }
  }

  // Handle simple number value
  if (typeof value === 'number' && value !== 0) {
    const currencySymbol = config.currencySymbol || '$'
    const formattedValue = formatNumber(value)
    return `${currencySymbol}${formattedValue}`
  }

  return ''
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate URL format
 * @param value - URL string to validate
 * @returns True if valid URL, false otherwise
 */
const isValidUrl = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false
  }

  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

/**
 * Validate email format
 * @param value - Email string to validate
 * @returns True if valid email, false otherwise
 */
const isValidEmail = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(value)
}

/**
 * Validate required field
 * @param value - Value to validate
 * @returns True if value is not empty, false otherwise
 */
export const isRequiredFieldValid = (value: any): boolean => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  return true
}

// ============================================================================
// Input Sanitization
// ============================================================================

/**
 * Sanitize text input by removing potentially harmful characters
 * @param value - Input value to sanitize
 * @returns Sanitized value
 */
const sanitizeTextInput = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return value
  }

  // Remove HTML tags and potentially harmful characters
  return value
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>]/g, '') // Remove remaining angle brackets
    .trim()
}

/**
 * Sanitize number input by removing non-numeric characters
 * @param value - Input value to sanitize
 * @param allowDecimal - Whether to allow decimal point
 * @returns Sanitized value
 */
const sanitizeNumberInput = (value: string, allowDecimal: boolean = true): string => {
  if (!value || typeof value !== 'string') {
    return value
  }

  const pattern = allowDecimal ? /[^0-9.-]/g : /[^0-9-]/g
  return value.replace(pattern, '')
}

// ============================================================================
// Default Formatters and Parsers
// ============================================================================

/**
 * Read-mode display of a DATE field. Honours the field's `format` (the same
 * string the DatePicker edits with) so a field reads the same way whether or
 * not it is being edited; without one it falls back to the browser locale as
 * before. Parses through dayjs, which reads a date-only string as LOCAL
 * midnight — `new Date('2026-03-05')` is UTC midnight and rendered a day early
 * in any timezone west of Greenwich.
 */
export function formatDateDisplay(value: FieldValue, format?: string): string {
  if (value === null || value === undefined || value === '') return ''
  const parsed = dayjs(value as string | number | Date)
  if (!parsed.isValid()) return String(value)
  return format ? parsed.format(format) : parsed.toDate().toLocaleDateString()
}

/**
 * Default formatters for different field types
 * Keyed by field type; the field value is always the first argument, a few accept extra context after it.
 */
type DefaultFormatterMap = Partial<
  Record<FieldTypeEnum, (value: FieldValue, ...context: never[]) => ReturnType<DisplayFormatter>>
>

export const defaultFormatters: DefaultFormatterMap = {
  text: (value: any) => String(value || ''),
  number: formatNumber,
  currency: (value: any) => formatCurrency(value, '$', true),
  select: (value: any, options?: SelectListEntry[]) =>
    options ? getSelectDisplayValue(value, options) : String(value || ''),
  textarea: (value: any) => String(value || ''),
  url: (value: any) => String(value || ''),
  company: (value: any, getCompanyDetailsById?: (id: string) => any, mode: 'single' | 'multiple' = 'single') =>
    getCompanyDisplayValue(value, getCompanyDetailsById, mode),
  country: getCountryDisplayValue,
  city: getCityDisplayValue,
  contact: (value: any, mode: 'single' | 'multiple' = 'single') => getContactDisplayValue(value, mode),
  valuation: (value: any, config: any) => getValuationDisplayValue(value, config),
  date: (value: any) => formatDateDisplay(value),
  time: (value: any) => (value ? String(value) : '')
}

/**
 * Default parsers for different field types
 */
const defaultParsers = {
  text: (value: string) => sanitizeTextInput(value),
  number: parseNumber,
  currency: (value: string) => parseCurrency(value, '$'),
  select: (value: any) => value,
  textarea: (value: string) => sanitizeTextInput(value),
  url: (value: string) => sanitizeTextInput(value),
  company: (value: any) => value,
  country: (value: any) => value,
  city: (value: any) => value,
  contact: (value: any) => value,
  valuation: (value: any) => value,
  date: (value: any) => value
}
